import { createContext, useContext, useState, useEffect, ReactNode, useMemo } from 'react';
import { getGrupoByEmpresaId, isEmpresaNoGrupo } from '@/mocks/admin/gruposEmpresas';

export interface Empresa {
  id: number;
  nome: string;
  cnpj: string;
  ativa: boolean;
  empresaBackofficeId?: string;
  grupoId?: string;
}

interface EmpresaContextType {
  empresaSelecionada: Empresa;
  empresas: Empresa[];
  todasEmpresas: Empresa[];
  selecionarEmpresa: (empresa: Empresa) => void;
  carregarDadosEmpresa: () => void;
  definirEmpresasPermitidas: (ids: number[] | null) => void;
  empresaPertenceAGrupo: boolean;
  grupoEmpresaIds: string[];
}

const EmpresaContext = createContext<EmpresaContextType | undefined>(undefined);

const TODAS_EMPRESAS: Empresa[] = [
  {
    id: 1,
    nome: 'getPoints',
    cnpj: '12.345.678/0001-90',
    ativa: true,
    empresaBackofficeId: 'EMP-001',
    grupoId: 'GRUPO-001',
  },
  {
    id: 2,
    nome: 'Inovação Digital S.A.',
    cnpj: '98.765.432/0001-10',
    ativa: false,
    empresaBackofficeId: 'EMP-002',
    grupoId: 'GRUPO-002',
  },
  {
    id: 3,
    nome: 'Consultoria Tech Brasil',
    cnpj: '45.678.901/0001-23',
    ativa: false,
    empresaBackofficeId: 'EMP-003',
    grupoId: 'GRUPO-001',
  },
  {
    id: 4,
    nome: 'Sistemas Integrados Ltda',
    cnpj: '23.456.789/0001-45',
    ativa: false,
    empresaBackofficeId: 'EMP-004',
    grupoId: 'GRUPO-002',
  }
];

// Mapeamento: empresaId (número do contexto) → empresaBackofficeId (string do mock)
export const EMPRESA_ID_MAP: Record<number, string> = {
  1: 'EMP-001',
  2: 'EMP-002',
  3: 'EMP-003',
  4: 'EMP-004'
};

export const EMPRESA_ID_MAP_REVERSE: Record<string, number> = {
  'EMP-001': 1,
  'EMP-002': 2,
  'EMP-003': 3,
  'EMP-004': 4
};

export function EmpresaProvider({ children }: { children: ReactNode }) {
  const [empresasPermitidasIds, setEmpresasPermitidasIds] = useState<number[] | null>(null);

  // Filtrar primeiro por permissões (backoffice), depois por grupo
  const empresasFiltradas = useMemo(() => {
    let filtradas = empresasPermitidasIds === null
      ? TODAS_EMPRESAS
      : TODAS_EMPRESAS.filter(e => empresasPermitidasIds.includes(e.id));
    return filtradas;
  }, [empresasPermitidasIds]);

  const [empresaSelecionada, setEmpresaSelecionada] = useState<Empresa>(
    empresasFiltradas.find(e => e.ativa) || empresasFiltradas[0]
  );

  // Filtrar empresas visíveis pelo grupo da empresa selecionada
  const empresasVisiveis = useMemo(() => {
    if (!empresaSelecionada.grupoId) return empresasFiltradas;
    
    // Se a empresa pertence a um grupo, só mostrar empresas do mesmo grupo
    const grupo = getGrupoByEmpresaId(empresaSelecionada.empresaBackofficeId || '');
    if (!grupo || !grupo.ativo) return empresasFiltradas;

    // Filtrar empresas do mesmo grupo
    const idsDoGrupo = grupo.membros;
    return empresasFiltradas.filter(e => 
      idsDoGrupo.includes(e.empresaBackofficeId || '')
    );
  }, [empresasFiltradas, empresaSelecionada]);

  const empresaPertenceAGrupo = useMemo(() => {
    return isEmpresaNoGrupo(empresaSelecionada.empresaBackofficeId || '');
  }, [empresaSelecionada]);

  const grupoEmpresaIds = useMemo(() => {
    if (!empresaSelecionada.grupoId) return [];
    const grupo = getGrupoByEmpresaId(empresaSelecionada.empresaBackofficeId || '');
    return (grupo && grupo.ativo) ? grupo.membros : [];
  }, [empresaSelecionada]);

  const definirEmpresasPermitidas = (ids: number[] | null) => {
    console.log('🔐 Definindo empresas permitidas:', ids);
    setEmpresasPermitidasIds(ids);
  };

  // Ajustar empresa selecionada quando filtro muda
  useEffect(() => {
    if (empresasPermitidasIds !== null) {
      const estaPermitida = empresasFiltradas.find(e => e.id === empresaSelecionada.id);
      if (!estaPermitida) {
        const primeiraPermitida = empresasFiltradas.find(e => e.ativa) || empresasFiltradas[0];
        if (primeiraPermitida) {
          console.log('🔄 Empresa atual não permitida, trocando para:', primeiraPermitida.nome);
          setEmpresaSelecionada(primeiraPermitida);
          localStorage.setItem('empresaSelecionadaId', primeiraPermitida.id.toString());
        }
      }
    }
  }, [empresasPermitidasIds, empresasFiltradas]);

  const selecionarEmpresa = (empresa: Empresa) => {
    console.log('🔄 Alterando empresa de:', empresaSelecionada.nome, 'para:', empresa.nome);

    // Validação: se a empresa atual pertence a um grupo, só pode trocar dentro do mesmo grupo
    if (empresaSelecionada.grupoId && empresa.grupoId !== empresaSelecionada.grupoId) {
      console.warn('⛔ Troca bloqueada: empresa fora do grupo');
      return;
    }

    setEmpresaSelecionada(empresa);
    localStorage.setItem('empresaSelecionadaId', empresa.id.toString());

    setTimeout(() => {
      carregarDadosEmpresa(empresa);
    }, 100);
  };

  const carregarDadosEmpresa = (empresa?: Empresa) => {
    const empresaAtual = empresa || empresaSelecionada;
    console.log('📊 Carregando dados da empresa:', empresaAtual.id, '-', empresaAtual.nome);

    const evento = new CustomEvent('empresaAlterada', {
      detail: {
        empresaId: empresaAtual.id,
        empresaNome: empresaAtual.nome,
        empresaCnpj: empresaAtual.cnpj
      }
    });

    window.dispatchEvent(evento);

    console.log('🚀 Evento empresaAlterada disparado para empresa:', empresaAtual.nome);
  };

  // Carregar empresa salva no localStorage ao iniciar
  useEffect(() => {
    const empresaIdSalva = localStorage.getItem('empresaSelecionadaId');
    if (empresaIdSalva) {
      const empresa = empresasFiltradas.find(e => e.id === parseInt(empresaIdSalva));
      if (empresa && empresa.id !== empresaSelecionada.id) {
        console.log('🔄 Carregando empresa salva do localStorage:', empresa.nome);
        setEmpresaSelecionada(empresa);
        setTimeout(() => {
          carregarDadosEmpresa(empresa);
        }, 100);
      }
    }
  }, [empresasFiltradas]);

  // Carregar dados iniciais
  useEffect(() => {
    carregarDadosEmpresa();
  }, []);

  return (
    <EmpresaContext.Provider value={{
      empresaSelecionada,
      empresas: empresasVisiveis,
      todasEmpresas: TODAS_EMPRESAS,
      selecionarEmpresa,
      carregarDadosEmpresa,
      definirEmpresasPermitidas,
      empresaPertenceAGrupo,
      grupoEmpresaIds
    }}>
      {children}
    </EmpresaContext.Provider>
  );
}

export function useEmpresa() {
  const context = useContext(EmpresaContext);
  if (context === undefined) {
    throw new Error('useEmpresa deve ser usado dentro de um EmpresaProvider');
  }
  return context;
}